import React from "react";
import { Alert, Box, Button, Card, Column, Columns, Stack, Text, TextField } from "braid-design-system";
import { useAuth } from "../contexts/AuthProvider";
import ForbiddenAccessPage from "./ForbiddenAccessPage";
import { getUserSignOnRecords, updateUserSignOnRecords, UserSignOnRecord, getServerDateTime, ServerDateTime } from "../services/signOnService";

const normalizeRole = (value: string | null | undefined): string =>
    (value || "").trim().toUpperCase();

const canAccessSignOn = (role: string | null | undefined): boolean => {
    const normalized = normalizeRole(role);
    return normalized === "CASHIER" || normalized === "ADMIN";
};

const STATION_ID = "001";

const SignOn: React.FC = () => {
    const { user } = useAuth();
    const hasSignOnAccess = canAccessSignOn(user?.user_role as string | null | undefined);
    const [serverDateTime, setServerDateTime] = React.useState<ServerDateTime | null>(null);
    const [clientNow, setClientNow] = React.useState(() => new Date());
    const userName = user?.user_name || "-";
    const [signOnRecord, setSignOnRecord] = React.useState<UserSignOnRecord | null>(null);
    const [isLoading, setIsLoading] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const [openingBalance, setOpeningBalance] = React.useState("");
    const [openingBalanceError, setOpeningBalanceError] = React.useState<string | null>(null);

    React.useEffect(() => {
        const timer = setInterval(() => setClientNow(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    // Fetch server datetime on component mount
    React.useEffect(() => {
        if (!hasSignOnAccess) {
            return;
        }

        const fetchServerDateTime = async () => {
            try {
                const dt = await getServerDateTime();
                setServerDateTime(dt);
            } catch {
                // Silently fail - will use fallback from service
            }
        };
        fetchServerDateTime();
    }, [hasSignOnAccess]);

    const clientDate = React.useMemo(() => {
        const yyyy = String(clientNow.getFullYear());
        const mm = String(clientNow.getMonth() + 1).padStart(2, "0");
        const dd = String(clientNow.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
    }, [clientNow]);

    const clientTime = React.useMemo(() => {
        const hh = String(clientNow.getHours()).padStart(2, "0");
        const min = String(clientNow.getMinutes()).padStart(2, "0");
        const ss = String(clientNow.getSeconds()).padStart(2, "0");
        return `${hh}:${min}:${ss}`;
    }, [clientNow]);

    const handleOpeningBalanceChange = (value: string) => {
        if (/^\d*$/.test(value)) {
            setOpeningBalance(value);
            setOpeningBalanceError(null);
            return;
        }
        setOpeningBalanceError("Opening Balance must contain numbers only");
    };

    const handleSignOn = async () => {
        setIsLoading(true);
        setErrorMessage(null);
        setSignOnRecord(null);
        try {
            if (!hasSignOnAccess) {
                setErrorMessage("Only users with CASHIER or ADMIN role can sign on.");
                return;
            }

            if (!user?.user_name) {
                setErrorMessage("User not found");
                return;
            }

            let latestServerDateTime: ServerDateTime | null = serverDateTime;
            try {
                const dt = await getServerDateTime();
                setServerDateTime(dt);
                latestServerDateTime = dt;
            } catch {
                // ignore
            }

            const userNameToCheck = user.user_name;
            const record = await getUserSignOnRecords(userNameToCheck);
            if (!record) {
                setErrorMessage(`No records found for user: ${userNameToCheck}`);
                return;
            }

            if (record.signon === 1) {
                // User already signed on - don't allow another sign on
                const stationId = record.stationid != null ? String(record.stationid) : "-";
                const signOnDate = record.signondate ?? "-";
                setErrorMessage(`User already sign on to ${stationId} on ${signOnDate}`);
                // Don't set signOnRecord to prevent success message from showing
                return;
            }

            // Validate opening balance is required
            if (openingBalance === "" || openingBalance === "0") {
                setOpeningBalanceError("Opening Balance is required");
                setIsLoading(false);
                return;
            }

            // User not signed on - update the record
            const openingBalanceNumber = parseFloat(openingBalance);
            const floatAmtValue = isNaN(openingBalanceNumber) ? 0 : openingBalanceNumber;

            const signOnDateToSend = (latestServerDateTime?.date || clientDate || "").trim();
            const signOnDateTimeToSend = (latestServerDateTime?.datetime || `${clientDate} ${clientTime}` || "").trim();
            
            const updatedRecord = await updateUserSignOnRecords({
                user_name: userNameToCheck,
                signon: 1,
                signondate: signOnDateToSend || undefined,
                signontime: signOnDateTimeToSend || undefined,
                station_id: STATION_ID,
                floatamt: floatAmtValue,
            });

            if (updatedRecord) {
                setSignOnRecord(updatedRecord);
                window.dispatchEvent(new CustomEvent('webpos:signon-status-changed'));
            } else {
                setErrorMessage(`Unable to update sign-on record for user: ${userNameToCheck}`);
            }
        } catch (error: any) {
            setErrorMessage(error.message || "Failed to retrieve sign-on records");
        } finally {
            setIsLoading(false);
        }
    };

    if (!hasSignOnAccess) {
        return <ForbiddenAccessPage message="Only users with CASHIER or ADMIN role can sign on." />;
    }

    return (
        <>
            <Box padding="medium">
                <Card>
                    <Box padding="medium">
                        <Stack space="medium">
                            <Text weight="strong" size="large">Cashier Sign On</Text>

                            <Columns space="medium" collapseBelow="tablet">
                                <Column>
                                    <Stack space="xsmall">
                                        <Text tone="secondary">User Name</Text>
                                        <Box background="surface" borderRadius="standard" padding="small">
                                            <div style={{ textAlign: "left" }}>
                                                <Text>{userName}</Text>
                                            </div>
                                        </Box>
                                    </Stack>
                                </Column>

                                <Column>
                                    <Stack space="xsmall">
                                        <Text tone="secondary">Station ID</Text>
                                        <Box background="surface" borderRadius="standard" padding="small">
                                            <div style={{ textAlign: "left" }}>
                                                <Text>{signOnRecord?.stationid != null ? String(signOnRecord.stationid) : STATION_ID}</Text>
                                            </div>
                                        </Box>
                                    </Stack>
                                </Column>

                                <Column>
                                    <Stack space="xsmall">
                                        <Text tone="secondary">Sign On Date</Text>
                                        <Box background="surface" borderRadius="standard" padding="small">
                                            <Text>{serverDateTime?.date || clientDate}</Text>
                                        </Box>
                                    </Stack>
                                </Column>

                                <Column>
                                    <Stack space="xsmall">
                                        <Text tone="secondary">Sign On Time</Text>
                                        <Box background="surface" borderRadius="standard" padding="small">
                                            <Text>{serverDateTime?.time || clientTime}</Text>
                                        </Box>
                                    </Stack>
                                </Column>

                            </Columns>

                            <Box>
                                <div style={{ width: "220px" }}>
                                    <TextField
                                        label="Openning Balance"
                                        value={openingBalance}
                                        onChange={(e) => handleOpeningBalanceChange(e.currentTarget.value)}
                                        tone={openingBalanceError ? "critical" : undefined}
                                        message={openingBalanceError || undefined}
                                    />
                                </div>
                            </Box>

                            <Box>
                                <div style={{ width: "140px" }}>
                                    <Button variant="solid" tone="brandAccent" onClick={handleSignOn} loading={isLoading}>
                                        Sign On
                                    </Button>
                                </div>
                            </Box>

                            {errorMessage ? (
                                <Alert tone="critical">
                                    { errorMessage && <Text>{errorMessage}</Text> }
                                </Alert>
                            ) : null}

                            {signOnRecord !== null && signOnRecord.signon === 1 ? (
                                <Alert tone="positive">
                                    <div style={{ textAlign: "left" }}>
                                        <Text>
                                            Login user sign on Completed Signondate: {signOnRecord.signondate ?? "-"} to Stationid: {signOnRecord.stationid != null ? String(signOnRecord.stationid) : "-"}
                                        </Text>
                                    </div>
                                </Alert>
                            ) : null}

                        </Stack>
                    </Box>
                </Card>
            </Box>
        </>
    );
};

export default SignOn;
